import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";

import instance from "../api/axios";
import {
  confirmHoldBookingByToken,
  getHoldBookingByToken,
} from "../api/route";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import DoctorAssignedCard from "../components/SessionDetail/DoctorAssignedCard";
import AppointmentDetailsCard from "../components/SessionDetail/AppointmentDetailsCard";
import PatientInfoCard from "../components/SessionDetail/PatientInfoCard";
import AddressCard from "../components/SessionDetail/AddressCard";
import Button from "../components/Button";
import mixpanel from "../mixpanel";
import HoldBookingConfirmation from "./HoldBookingConfirmation";
import {
  formatCountdown,
  formatDisplayDate,
  formatDisplayTime,
  getRemainingTimeMs,
} from "../utils/holdBooking";

const libraries = ["places"];

const TimerBanner = ({ remainingMs }) => {
  const isLowTimer = remainingMs < 2 * 60 * 1000;

  return (
    <div
      className="mx-4 mb-4 rounded-2xl bg-white p-5 shadow-sm"
      style={{ color: "#111111" }}
    >
      <div className="text-lg font-semibold">
        {isLowTimer
          ? "Still interested in this appointment?"
          : "Your selected appointment is on hold while you complete your booking"}
      </div>
      <div
        className="mt-3 text-3xl font-bold"
        style={{ color: "#1E3A8A" }}
      >
        {formatCountdown(remainingMs)}
      </div>
      <div className="mt-3 text-base leading-7 text-black">
        {isLowTimer
          ? "Please confirm your address to complete your booking."
          : "Please add your address within the time above so we can confirm your therapist visit and complete your booking."}
      </div>
      {!isLowTimer && (
        <div className="mt-3 text-[13px] font-semibold  text-[#6B7280]">
          Your physiotherapist and appointment time will remain reserved while you complete this step.
        </div>
      )}
    </div>
  );
};

const StateCard = ({ title, description, actionLabel, onAction }) => (
  <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#FFF8F2]">
    <div className="w-full max-w-[480px] rounded-[28px] bg-white shadow-lg p-8 text-center">
      <h1 className="text-3xl font-bold text-[#222]">{title}</h1>
      <p className="mt-4 text-sm leading-6 text-[#666]">{description}</p>
      <Button
        btnName={actionLabel}
        className="offer-gradient text-white px-6 py-3 mt-6"
        onClick={onAction}
      />
    </div>
  </div>
);

const CompleteHoldBookingPage = () => {
  const { secureToken } = useParams();
  const navigate = useNavigate();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBqKbHptbwiV5W2wsYy0J1G1x2AKk2aeRU",
    libraries,
  });

  const [pageState, setPageState] = useState("loading");
  const [hold, setHold] = useState(null);
  const [booking, setBooking] = useState(null);
  const [address, setAddress] = useState("");
  const [floor, setFloor] = useState("");
  const [autocomplete, setAutocomplete] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadHold = useCallback(async () => {
    try {
      const response = await instance.get(`${getHoldBookingByToken}/${secureToken}`);
      const nextState = response?.data?.state || "invalid";
      const nextHold = response?.data?.hold || null;
      const nextBooking = response?.data?.booking || null;
      setHold(nextHold);
      setBooking(nextBooking);
      setPageState(nextState);
      setRemainingMs(getRemainingTimeMs(nextHold?.expires_at));

      mixpanel.track("Hold Booking Link Opened", {
        secure_token: secureToken,
        hold_id: nextHold?.hold_id,
        state: nextState,
      });
    } catch (error) {
      const state = error?.response?.data?.state || "invalid";
      setPageState(state);
      setErrorMessage(error?.response?.data?.message || "Unable to load booking link.");
    }
  }, [secureToken]);

  useEffect(() => {
    loadHold();
  }, [loadHold]);

  useEffect(() => {
    if (!hold?.expires_at || pageState !== "active") return undefined;
    const intervalId = setInterval(() => {
      const nextRemaining = getRemainingTimeMs(hold.expires_at);
      setRemainingMs(nextRemaining);
      if (nextRemaining <= 0) {
        setPageState("expired");
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [hold?.expires_at, pageState]);

  const onLoad = useCallback((instanceValue) => {
    setAutocomplete(instanceValue);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place?.geometry?.location) return;
    const location = place.geometry.location;
    setCoordinates({ lat: location.lat(), lng: location.lng() });
    setAddress(place.formatted_address || "");
  }, [autocomplete]);

  const handleConfirm = async () => {
    const finalAddress = `${floor} ${address}`.trim();
    if (!finalAddress) return;

    setSubmitting(true);
    setErrorMessage("");
    try {
      const response = await instance.post(
        `${confirmHoldBookingByToken}/${secureToken}/confirm`,
        {
          address: finalAddress,
          latitude: coordinates?.lat ?? null,
          longitude: coordinates?.lng ?? null,
        }
      );

      mixpanel.track("Hold Booking Confirmed", {
        hold_id: hold?.hold_id,
        booking_id: response?.data?.booking_id,
      });

      navigate(`/complete-booking/${secureToken}/success`, {
        state: {
          booking: response?.data?.booking,
          hold: response?.data?.hold,
        },
      });
    } catch (error) {
      const nextState = error?.response?.data?.state;
      if (nextState === "expired" || nextState === "completed" || nextState === "invalid") {
        setPageState(nextState);
      }
      setErrorMessage(
        error?.response?.data?.message || "Unable to confirm booking right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <StateCard
        title="Map Loading Failed"
        description="Google Maps could not be loaded on this device right now. Please refresh the page or contact our team."
        actionLabel="Back To Home"
        onAction={() => navigate("/")}
      />
    );
  }

  if (pageState === "loading" || !isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center text-base font-semibold text-[#393938]">
        Loading your reserved session...
      </div>
    );
  }

  if (pageState === "expired") {
    return (
      <StateCard
        title="This Slot Hold Has Expired"
        description={errorMessage || "Please contact our team so they can send you a fresh confirmation link."}
        actionLabel="Back To Home"
        onAction={() => navigate("/")}
      />
    );
  }

  if (pageState === "completed") {
    if (booking && hold) {
      return (
        <HoldBookingConfirmation
          bookingData={booking}
          holdData={hold}
          alreadyConfirmed
        />
      );
    }
    return (
      <StateCard
        title="Your Booking Is Already Confirmed"
        description={
          hold?.booking_id
            ? `Your booking has already been confirmed. Booking ID: ${hold.booking_id}`
            : "This confirmation link has already been used successfully."
        }
        actionLabel="Back To Home"
        onAction={() => navigate("/")}
      />
    );
  }

  if (pageState !== "active" || !hold) {
    return (
      <StateCard
        title="Invalid Confirmation Link"
        description={errorMessage || "This booking link is invalid or no longer available."}
        actionLabel="Back To Home"
        onAction={() => navigate("/")}
      />
    );
  }

  const buttonDisabled = address.trim() === "" || floor.trim() === "" || submitting;

  return (
    <div>
      <div className="min-h-screen md:px-0 flex justify-center px-0 py-0 md:py-6 md:px-4">
        <div
          className="w-full max-w-[700px] md:rounded-2xl md:shadow-lg md:bg-cover md:bg-no-repeat"
          style={{
            backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
          }}
        >
          <div className="max-w-[700px] mx-auto">
            <BackHeader onBack={() => navigate("/")} />
            <StepIndicator step={1} totalSteps={1} header="Confirm Your Session" />
            <TimerBanner remainingMs={remainingMs} />
            <DoctorAssignedCard doctorName={`Dr. ${hold.therapist_name}`} />
            <AppointmentDetailsCard
              date={formatDisplayDate(hold.start_time)}
              time={formatDisplayTime(hold.start_time)}
              price={`Rs ${hold.amount}`}
            />
            <PatientInfoCard
              patientName={hold.patient_name}
              patientPhone={hold.patient_phone}
              pincode="N/A"
              condition={hold.pain_condition || "N/A"}
            />

            <div className="bg-white mx-3 mt-4 rounded-3xl p-5 shadow-md">
              <div className="text-lg font-semibold text-[#2E2E2E]">Pain Details</div>
              <div className="mt-3 text-sm text-[#5A5A5A]">
                Pain Score: <span className="font-semibold text-[#1D1617]">{hold.pain_score || "N/A"}</span>
              </div>
              <div className="mt-2 text-sm text-[#5A5A5A]">
                Pain Duration: <span className="font-semibold text-[#1D1617]">{hold.pain_duration || "N/A"}</span>
              </div>
            </div>

            <AddressCard
              address={address}
              setAddress={setAddress}
              floor={floor}
              setFloor={setFloor}
              buttonDisabled={buttonDisabled}
              onLoad={onLoad}
              onPlaceChanged={onPlaceChanged}
            />

            {errorMessage && (
              <div className="px-6 pb-2 text-center text-sm font-semibold text-red-600">
                {errorMessage}
              </div>
            )}

            <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-[#E5E7EB] px-4 py-4 mt-4">
              <Button
                btnName={submitting ? "Confirming Booking..." : "Confirm Booking"}
                className={`offer-gradient w-full text-center text-white font-semibold text-lg px-14 py-3 rounded-xl ${buttonDisabled ? "cursor-not-allowed opacity-50" : ""
                  }`}
                onClick={handleConfirm}
                disabled={buttonDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteHoldBookingPage;
